import { FixedTask, SagaGenerator, call, cancel, delay, fork, take, put, select } from "typed-redux-saga";
import { IsInterface, isNumber } from "generic-type-guard";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { AuthenticationState, UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { fetchWrapper } from "../../../app/sagas/fetchSagaFactory";
import { TASK_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { selectOperationLockState } from "../../../app/slices/operation/selectors";
import { shouldRenewLockProcess } from "../../../utilities/lockProcessUtils";

import { renewLockProcess, startLockProcessPolling, stopLockProcessPolling, unlockProcess } from "../store/actions";

const isLockConfig = new IsInterface().withProperties({ renewIntervalSeconds: isNumber }).get();

const FALLBACK_RENEW_INTERVAL_MS = 5_000;

// The backend guarantees the interval is below de.skhh.modulf.lock.process.expiration.seconds
async function fetchRenewIntervalMs(): Promise<number> {
	try {
		const response = await fetchWrapper({ method: "GET", relativeUrl: "/lock-config" });
		const data: unknown = await response.json();
		if (isLockConfig(data) && data.renewIntervalSeconds > 0) {
			return data.renewIntervalSeconds * 1000;
		}
	} catch {
		// fall back to the default
	}
	return FALLBACK_RENEW_INTERVAL_MS;
}

function* pollingWorker(docRef: string | undefined): SagaGenerator<void> {
	const renewIntervalMs = yield* call(fetchRenewIntervalMs);
	while (docRef) {
		yield* delay(renewIntervalMs);
		const authenticationState = yield* select(UaaSelectors.state);
		if (authenticationState !== AuthenticationState.AUTHENTICATED) {
			continue;
		}
		const exists = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
		if (!exists) {
			yield* put(unlockProcess({ docRef }));
			break;
		} else {
			const lockState = yield* select(selectOperationLockState(docRef));
			const user = yield* select(UaaSelectors.user);
			if (shouldRenewLockProcess(lockState, user)) {
				yield* put(renewLockProcess({ docRef }));
			}
		}
	}
}

let pollingTask: FixedTask<void> | null = null;

export function* pollingLockProcessSaga(): SagaGenerator<void> {
	while (true) {
		const action = yield* take([startLockProcessPolling, stopLockProcessPolling]);

		if (action.type === startLockProcessPolling.type && (!pollingTask || !pollingTask.isRunning())) {
			pollingTask = yield* fork(pollingWorker, action.payload.docRef);
		}

		if (action.type === stopLockProcessPolling.type && pollingTask) {
			yield* cancel(pollingTask);
			pollingTask = null;
		}
	}
}
